import { Button } from "@/components/ui/button";
import { FloatingInput } from "@/components/ui/floating-input";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { isValidEmail, mapAuthError } from "./auth-utils";
import { FormErrorMessage } from "./form-error";
import { PasswordField } from "./password-field";

interface LoginFormProps {
  /**
   * Switches the page to the `forgot` stage (legacy `onForgotClick`). Carries whatever
   * the member typed in the email box so the forgot form can prefill it (BDAY-474).
   */
  onForgotClick: (typedEmail: string) => void;
  /** Switches the page to `register`. Omitted when the caller wants login only. */
  onSwitch?: () => void;
  /** Pre-seeded error, e.g. an `?error=` code bounced back by an OAuth callback. */
  initialError?: string;
  /** Prefill (an address the page's email link proves — lib/proven-email). */
  defaultEmail?: string;
}

/**
 * Port of legacy `components/auth/login`. Field order, labels, button copy and
 * every validation string are the old ones; the visual order (fields → forgot
 * link → button → error) reproduces the `order:` rules the old stylesheet
 * applied to `.login-container`.
 */
export function LoginForm({ onForgotClick, onSwitch, initialError, defaultEmail }: LoginFormProps) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean }>({});
  const [general, setGeneral] = useState(initialError ?? "");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const errors = {
    email: !email.trim()
      ? "Email is required!"
      : !isValidEmail(email)
        ? "Email is invalid"
        : undefined,
    password: !password ? "Password is required!" : undefined,
  };
  const hasErrors = !!(errors.email || errors.password);

  const loginMutation = useMutation({
    mutationFn: () =>
      edenFetch(api.api.public.auth.email.login.post({ email, password, platform: "web" })),
    onSuccess: () => {
      // No navigation here: __root's logged-in branch owns the redirect (and the
      // `?next=` handoff), so password login, OAuth return and an already-signed-in
      // visitor all leave this page by exactly one code path.
      void queryClient.invalidateQueries({ queryKey: ["/api/user/auth/me"] });
    },
    onError: (err: Error) => setGeneral(mapAuthError(err.message)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setGeneral("");
    if (hasErrors) {
      // Move focus to the first problem — otherwise the button appears to do
      // nothing at all for anyone not watching the field turn red.
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    loginMutation.mutate();
  };

  return (
    <>
      {/* noValidate: the fields carry `required` / type="email" for semantics and
          autofill, but native constraint bubbles would fire first and the legacy
          validation strings below would never be reachable on submit. */}
      <form
        noValidate
        onSubmit={submit}
        className="flex w-full max-w-[450px] flex-col items-center"
      >
        <div className="mb-[15px] w-full">
          <FloatingInput
            ref={emailRef}
            label="Email"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            error={touched.email ? errors.email : undefined}
            data-testid="input-email-login"
          />
        </div>
        <div className="mb-[15px] w-full">
          <PasswordField
            ref={passwordRef}
            label="Password"
            id="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            error={touched.password ? errors.password : undefined}
            data-testid="input-password-login"
          />
        </div>

        <div className="w-full text-left sm:text-right">
          <button
            type="button"
            onClick={() => onForgotClick(email.trim())}
            className="text-sm text-primary underline hover:text-primary/80"
            data-testid="link-forgot-password"
          >
            Forgot password?
          </button>
        </div>

        <div className="mt-4">
          <Button
            type="submit"
            size="compact"
            className="min-h-[39px] w-[199px] text-sm tracking-normal normal-case"
            disabled={loginMutation.isPending}
            data-testid="button-email-login"
          >
            Sign In with Email
          </Button>
        </div>

        {general && <FormErrorMessage>{general}</FormErrorMessage>}
      </form>

      {onSwitch && (
        // mt-4 — matches register-form, so the dialog doesn't shift when it swaps
        // between the two stages. See the note there for legacy's source value.
        <div className="mt-4 flex items-center justify-center gap-1.5 text-sm">
          <span>Don&rsquo;t have an account:</span>
          <button
            type="button"
            onClick={onSwitch}
            className="text-primary underline hover:text-primary/80"
            data-testid="button-switch-signup"
          >
            Register
          </button>
        </div>
      )}
    </>
  );
}
